// ============================================================
// CLUBHUB – Zuordnungen und Mandate: die Ansicht in der Adresse
// ============================================================
// Wie jede Liste im Haus (`lib/mitglieder/ansicht.ts`): Filter,
// Suche und Sortierung stehen in den searchParams. „Lücken“ ist
// ein Filter und kein Schild in jeder Zeile — die Zeilen tragen
// nur ein kleines Zeichen, der Filter sammelt sie.
// ============================================================
import { readSort, type SortColumns, type SortState } from "@/lib/sorting";
import type { MandatView, ZuordnungView } from "./views";

const eins = (p: Record<string, string | string[] | undefined>, name: string) => {
  const w = p[name];
  return ((Array.isArray(w) ? w[0] : w) ?? "").trim();
};

// ── Zuordnungen ─────────────────────────────────────────────
export const PRUEFUNGEN = [
  { schluessel: "luecken", label: "Lücken", hinweis: "Alles, was beim nächsten Lauf Ärger macht" },
  { schluessel: "mandat", label: "Lastschrift ohne gültiges Mandat", hinweis: "Zahlart Lastschrift, aber das Mandat fehlt, ist widerrufen, verfallen oder unvollständig" },
  { schluessel: "familie-teurer", label: "Familie teurer als einzeln", hinweis: "Der Familienbeitrag kostet mehr als die Einzelbeiträge" },
  { schluessel: "altersgrenze", label: "Altersgrenze", hinweis: "Das Alter passt nicht zur Beitragsart" },
  { schluessel: "abweichung", label: "Abweichender Betrag", hinweis: "Mit Sonderbetrag und Grund" },
  { schluessel: "familie", label: "Über Familie", hinweis: "Zahlt über die Zahlperson eines Haushalts" },
] as const;
export type Pruefung = (typeof PRUEFUNGEN)[number]["schluessel"] | "";

export const STAENDE = [
  { schluessel: "", label: "Gilt heute" },
  { schluessel: "beendet", label: "Beendet" },
  { schluessel: "alle", label: "Alle" },
] as const;
export type Stand = (typeof STAENDE)[number]["schluessel"];

export const Z_SORT = {
  name: { label: "Name", first: "auf" },
  beitrag: { label: "Beitragsart", first: "auf" },
  betrag: { label: "Betrag", first: "ab" },
  seit: { label: "Gültig ab", first: "ab" },
} as const satisfies SortColumns<string>;
export type ZSortKey = keyof typeof Z_SORT;

export type ZuordnungAnsicht = {
  q: string;
  beitrag: string;
  zahlart: "" | "SEPA" | "TRANSFER";
  pruefung: Pruefung;
  stand: Stand;
  sort: SortState<ZSortKey>;
};

export const Z_PARAMS = ["q", "beitrag", "zahlart", "pruefung", "stand", "sortierung", "richtung", "umfang"] as const;

export function zuordnungAnsicht(p: Record<string, string | string[] | undefined>): ZuordnungAnsicht {
  const pr = eins(p, "pruefung");
  const st = eins(p, "stand");
  const za = eins(p, "zahlart").toUpperCase();
  return {
    q: eins(p, "q"),
    beitrag: eins(p, "beitrag"),
    zahlart: za === "SEPA" || za === "TRANSFER" ? za : "",
    pruefung: (PRUEFUNGEN.some((x) => x.schluessel === pr) ? pr : "") as Pruefung,
    stand: (st === "beendet" || st === "alle" ? st : "") as Stand,
    sort: readSort(p, Z_SORT, "name"),
  };
}

export function zuordnungFilter(a: ZuordnungAnsicht): number {
  return [a.q, a.beitrag, a.zahlart, a.pruefung, a.stand].filter(Boolean).length;
}

type ZDim = "pruefung" | "zahlart" | "beitrag" | "stand";

export function zuordnungPasst(z: ZuordnungView, a: ZuordnungAnsicht, heute: string, ausser?: ZDim): boolean {
  const beendet = Boolean(z.validUntil && z.validUntil < heute);
  if (ausser !== "stand") {
    if (a.stand === "" && beendet) return false;
    if (a.stand === "beendet" && !beendet) return false;
  }
  if (ausser !== "beitrag" && a.beitrag && z.feeTypeId !== a.beitrag) return false;
  if (ausser !== "zahlart" && a.zahlart && (a.zahlart === "SEPA" ? z.paymentMethod !== "SEPA" : z.paymentMethod === "SEPA")) return false;
  if (ausser !== "pruefung" && a.pruefung && !pruefungTrifft(z, a.pruefung)) return false;
  if (a.q) {
    const n = a.q.toLowerCase();
    const heu = [z.contactName, z.memberNumber, z.householdName, z.feeTypeName, z.mandateReference, z.payerName, z.note].filter(Boolean).join(" ").toLowerCase();
    if (!heu.includes(n)) return false;
  }
  return true;
}

export function pruefungTrifft(z: ZuordnungView, p: Exclude<Pruefung, "">): boolean {
  switch (p) {
    case "luecken":
      return z.luecken.length > 0;
    case "mandat":
    case "familie-teurer":
    case "altersgrenze":
      return z.luecken.some((l) => l.art === p);
    case "abweichung":
      return z.amountOverride !== null;
    case "familie":
      return z.householdId !== null;
  }
}

// ── Mandate ─────────────────────────────────────────────────
export const M_LAGEN = [
  { schluessel: "", label: "Gültig", hinweis: "Darf eingezogen werden" },
  { schluessel: "unvollstaendig", label: "Unvollständig", hinweis: "Es fehlt etwas — bis dahin keine Lastschrift" },
  { schluessel: "frst", label: "Erstlastschrift", hinweis: "Noch nie eingezogen (FRST)" },
  { schluessel: "widerrufen", label: "Widerrufen", hinweis: "Vom Kontoinhaber oder mit dem Austritt beendet" },
  { schluessel: "verfallen", label: "Verfallen", hinweis: "Zu lange nicht genutzt" },
  { schluessel: "alle", label: "Alle", hinweis: "Alle Mandate" },
] as const;
export type MLage = (typeof M_LAGEN)[number]["schluessel"];

export const M_SORT = {
  referenz: { label: "Referenz", first: "auf" },
  inhaber: { label: "Kontoinhaber", first: "auf" },
  unterschrift: { label: "Unterschrift", first: "ab" },
  genutzt: { label: "Zuletzt genutzt", first: "ab" },
} as const satisfies SortColumns<string>;
export type MSortKey = keyof typeof M_SORT;

export type MandatAnsicht = { q: string; lage: MLage; sort: SortState<MSortKey> };
export const M_PARAMS = ["q", "lage", "sortierung", "richtung", "umfang"] as const;

export function mandatAnsicht(p: Record<string, string | string[] | undefined>): MandatAnsicht {
  const l = eins(p, "lage");
  return {
    q: eins(p, "q"),
    lage: (M_LAGEN.some((x) => x.schluessel === l) ? l : "") as MLage,
    sort: readSort(p, M_SORT, "referenz"),
  };
}

export function mandatLageVon(m: MandatView): Exclude<MLage, "" | "alle" | "frst"> | "gueltig" {
  if (m.status === "REVOKED") return "widerrufen";
  if (m.status === "EXPIRED") return "verfallen";
  if (m.maengel.length > 0) return "unvollstaendig";
  return "gueltig";
}

export function mandatPasst(m: MandatView, a: MandatAnsicht, ohneLage = false): boolean {
  if (!ohneLage) {
    const lage = mandatLageVon(m);
    if (a.lage === "" && lage !== "gueltig") return false;
    if (a.lage === "frst" && !(lage === "gueltig" && m.sequence === "FIRST")) return false;
    if (a.lage !== "" && a.lage !== "alle" && a.lage !== "frst" && lage !== a.lage) return false;
  }
  if (a.q) {
    const n = a.q.toLowerCase();
    const heu = [m.reference, m.accountHolder, m.contactName, m.householdName, m.memberNumber, m.ibanLast4, m.bankName, ...m.usedByNames].filter(Boolean).join(" ").toLowerCase();
    if (!heu.includes(n)) return false;
  }
  return true;
}
